import type { BrandPresetId } from '@workspace/types';

export type { BrandPresetId };

/** Valores HSL sin wrapper `hsl()` — formato shadcn/Tailwind */
export interface BrandColorTokens {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  primary: string;
  primaryForeground: string;
  primary50: string;
  primary100: string;
  primary200: string;
  primary300: string;
  primary400: string;
  primary500: string;
  primary600: string;
  primary700: string;
  primary800: string;
  primary900: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarActive: string;
  sidebarBorder: string;
  radius: string;
}

export interface BrandPreset {
  id: BrandPresetId;
  name: string;
  tokens: BrandColorTokens;
}

function scalePrimary(h: number, s: number, baseL: number): BrandColorTokens['primary500'] {
  return `${h} ${s}% ${baseL}%`;
}

function buildPrimaryScale(h: number, s: number, baseL: number) {
  return {
    primary50: `${h} ${Math.max(s - 20, 30)}% 97%`,
    primary100: `${h} ${Math.max(s - 15, 35)}% 94%`,
    primary200: `${h} ${s}% 88%`,
    primary300: `${h} ${s}% 78%`,
    primary400: `${h} ${s}% 65%`,
    primary500: scalePrimary(h, s, baseL),
    primary600: `${h} ${s}% ${baseL - 6}%`,
    primary700: `${h} ${s}% ${baseL - 12}%`,
    primary800: `${h} ${s}% ${baseL - 18}%`,
    primary900: `${h} ${s}% ${baseL - 24}%`,
  };
}

const setupPrimary = buildPrimaryScale(0, 100, 45);
const microsystPrimary = buildPrimaryScale(221, 83, 53);

export const BRAND_PRESETS: Record<BrandPresetId, BrandPreset> = {
  setup: {
    id: 'setup',
    name: 'Setup',
    tokens: {
      background: '0 0% 99%',
      foreground: '0 0% 9%',
      card: '0 0% 100%',
      cardForeground: '0 0% 9%',
      primary: setupPrimary.primary500,
      primaryForeground: '0 0% 100%',
      ...setupPrimary,
      secondary: '0 0% 96%',
      secondaryForeground: '0 0% 9%',
      muted: '210 20% 96%',
      mutedForeground: '215 16% 47%',
      accent: '0 100% 96%',
      accentForeground: setupPrimary.primary700,
      destructive: '0 84% 60%',
      destructiveForeground: '0 0% 100%',
      border: '214 20% 92%',
      input: '214 20% 92%',
      ring: setupPrimary.primary500,
      sidebar: '0 0% 10%',
      sidebarForeground: '210 40% 96%',
      sidebarActive: setupPrimary.primary500,
      sidebarBorder: '0 0% 16%',
      radius: '0.75rem',
    },
  },
  microsyst: {
    id: 'microsyst',
    name: 'Microsyst',
    tokens: {
      background: '210 40% 98%',
      foreground: '222 47% 11%',
      card: '0 0% 100%',
      cardForeground: '222 47% 11%',
      primary: microsystPrimary.primary500,
      primaryForeground: '0 0% 100%',
      ...microsystPrimary,
      secondary: '214 32% 91%',
      secondaryForeground: '222 47% 11%',
      muted: '210 40% 96%',
      mutedForeground: '215 16% 47%',
      accent: '214 95% 93%',
      accentForeground: microsystPrimary.primary700,
      destructive: '0 84% 60%',
      destructiveForeground: '0 0% 100%',
      border: '214 32% 91%',
      input: '214 32% 91%',
      ring: microsystPrimary.primary500,
      sidebar: '222 47% 11%',
      sidebarForeground: '210 40% 98%',
      sidebarActive: microsystPrimary.primary500,
      sidebarBorder: '217 33% 17%',
      radius: '0.75rem',
    },
  },
};

export function isBrandPresetId(value: string): value is BrandPresetId {
  return value === 'setup' || value === 'microsyst';
}
